import React, { FC, PropsWithChildren } from 'react'
import { BotId } from '@feedyou/schema'
import styled from 'styled-components'
import { ChasingDots } from 'styled-spinkit'

import { TopHeader } from '../../../TopHeader'
import { useAccessLevel } from '../../../../store/user'
import { removeUnsafeElements } from './utils/removeUnsafeElements'
import { useFetchCallsTable } from './hooks/useFetchCallsTable'
import { endpointHelpers } from '../../../../features/serverBots/endpointHelpers'
import { useAppTheme } from '../../../../defaultTheme'
import { useStoreOpenBotIdToAppSlice } from '../../../../store/app/hooks/useStoreOpenBotIdToAppSlice'

type Props = {
  sourceUrl: string
  fullscreen: boolean
  botId: BotId
}

export const CallsTablePage: FC<Props> = ({ sourceUrl, fullscreen, botId }) => {
  const theme = useAppTheme()
  const canAccessCallsList = useAccessLevel()?.callsList
  const tableData = useFetchCallsTable(sourceUrl, botId, fullscreen)

  useStoreOpenBotIdToAppSlice(botId)

  const permissionsQueryResult = endpointHelpers.getCurrentUserPermissionsForCurrentBot.useQuery()
  const arePermissionsLoaded = permissionsQueryResult.isSuccess

  if (!canAccessCallsList && arePermissionsLoaded) {
    return (
      <Layout fullscreen={fullscreen}>
        <Error>Sorry, you don't have permission to access call info.</Error>
      </Layout>
    )
  }

  if (tableData === null && arePermissionsLoaded) {
    return (
      <Layout fullscreen={fullscreen}>
        <Error>Sorry, it is not possible to load call info right now.</Error>
      </Layout>
    )
  }

  if (!tableData) {
    return (
      <Layout fullscreen={fullscreen}>
        <ChasingDots color={theme?.loading} data-test="loading-table-data" />
      </Layout>
    )
  }

  return (
    <Layout fullscreen={fullscreen}>
      <FullPageTable dangerouslySetInnerHTML={{ __html: removeUnsafeElements(tableData) }} />
    </Layout>
  )
}

export type CallsTablePageProps = Props

const Layout: FC<PropsWithChildren<{ fullscreen?: boolean }>> = ({ fullscreen, children }) => {
  return (
    <>
      {!fullscreen && <TopHeader />}
      <Wrapper>{children}</Wrapper>
    </>
  )
}

const Wrapper = styled.div`
  margin: 20px;

  @media print {
    margin: 0;
  }
`

const Error = styled.div`
  text-align: center;
`

const FullPageTable = styled.div`
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
  word-wrap: break-word;
  white-space: normal;

  & table,
  & th,
  & td {
    border: 1px #c7d0d7 solid;
    padding: 5px;
    text-align: left;
    border-left-width: 0px;
    border-right-width: 0px;
  }

  & th {
    font-weight: bold;
    border-top-width: 0px;
    color: #628198;
  }

  & table {
    margin: 0;
    border: 1px #ddd solid;
    border-collapse: collapse;
  }

  & tr:last-child td {
    border-bottom-width: 0;
  }

  & td,
  & th {
    padding: 5px;
    border: 1px #ddd solid;
    border-collapse: collapse;
  }

  @media print {
    & table {
      width: 100%;
    }
    & td.no-print,
    & th.no-print {
      display: none;
    }
  }
`
